/**
 * Comprehensive Accessibility Utilities
 * Provides utilities for screen reader support, keyboard navigation,
 * high contrast mode, dynamic text scaling, voice control, and focus management
 */

import { AccessibilityInfo, Dimensions, Platform } from 'react-native';
import { colorTokens } from '../tokens/colors';

// Accessibility state types
export interface AccessibilityState {
  disabled?: boolean;
  selected?: boolean;
  checked?: boolean;
  busy?: boolean;
  expanded?: boolean;
  pressed?: boolean;
}

// Focus management types
export interface FocusableElement {
  id: string;
  ref: React.RefObject<any>;
  onFocus?: () => void;
  onBlur?: () => void;
}

// High contrast mode types
export interface HighContrastColors {
  foreground: string;
  background: string;
  border: string;
  accent: string;
}

/**
 * Screen Reader Support Utilities
 */

/**
 * Generate semantic accessibility label for screen readers
 */
export function generateAccessibilityLabel(
  element: string,
  action?: string,
  state?: string,
  context?: string
): string {
  const parts = [element];

  if (action) parts.push(action);
  if (state) parts.push(state);
  if (context) parts.push(context);

  return parts.join(', ');
}

/**
 * Generate accessibility hint for screen readers
 */
export function generateAccessibilityHint(
  action: string,
  result?: string,
  context?: string
): string {
  const parts = [action];

  if (result) parts.push(result);
  if (context) parts.push(context);

  return parts.join('. ');
}

/**
 * Check if screen reader is enabled
 */
export async function isScreenReaderEnabled(): Promise<boolean> {
  try {
    return await AccessibilityInfo.isScreenReaderEnabled();
  } catch {
    return false;
  }
}

/**
 * Announce message to screen reader
 */
export function announceToScreenReader(message: string): void {
  if (Platform.OS === 'ios') {
    AccessibilityInfo.announceForAccessibility(message);
  } else {
    AccessibilityInfo.announceForAccessibilityWithOptions(message, {
      queue: false,
    });
  }
}

/**
 * Announce for accessibility (alias for announceToScreenReader)
 */
export function announceForAccessibility(message: string): void {
  announceToScreenReader(message);
}

/**
 * Keyboard Navigation Support
 */

/**
 * Check if device supports keyboard navigation
 */
export function supportsKeyboardNavigation(): boolean {
  return Platform.OS === 'web' || Platform.OS === 'macos';
}

/**
 * Generate keyboard navigation props
 */
export function getKeyboardNavigationProps(
  onKeyPress?: (key: string) => void,
  onEnter?: () => void,
  onEscape?: () => void,
  onArrowUp?: () => void,
  onArrowDown?: () => void,
  onArrowLeft?: () => void,
  onArrowRight?: () => void
) {
  if (Platform.OS !== 'web') return {};

  return {
    onKeyDown: (event: KeyboardEvent) => {
      const key = event.key;

      switch (key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          onEnter?.();
          break;
        case 'Escape':
          event.preventDefault();
          onEscape?.();
          break;
        case 'ArrowUp':
          event.preventDefault();
          onArrowUp?.();
          break;
        case 'ArrowDown':
          event.preventDefault();
          onArrowDown?.();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          onArrowLeft?.();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onArrowRight?.();
          break;
        default:
          onKeyPress?.(key);
      }
    },
    tabIndex: 0,
  };
}

/**
 * High Contrast Mode Support
 */

/**
 * Check if high contrast mode is enabled
 */
export async function isHighContrastEnabled(): Promise<boolean> {
  try {
    if (Platform.OS === 'ios') {
      // iOS doesn't have isHighContrastEnabled, so we'll use a fallback
      return false;
    }
    // Android doesn't have a direct API for this, so we'll use a fallback
    return false;
  } catch {
    return false;
  }
}

/**
 * Get high contrast color variants
 */
export function getHighContrastColors(
  baseColors: {
    foreground: string;
    background: string;
    border?: string;
    accent?: string;
  },
  isHighContrast: boolean = false
): HighContrastColors {
  if (!isHighContrast) {
    return {
      foreground: baseColors.foreground,
      background: baseColors.background,
      border: baseColors.border || baseColors.foreground,
      accent: baseColors.accent || baseColors.foreground,
    };
  }

  // High contrast color adjustments
  const isDark = isDarkColor(baseColors.background);

  if (isDark) {
    return {
      foreground: '#FFFFFF',
      background: '#000000',
      border: '#FFFFFF',
      accent: '#FFFF00', // High contrast yellow
    };
  } else {
    return {
      foreground: '#000000',
      background: '#FFFFFF',
      border: '#000000',
      accent: '#0000FF', // High contrast blue
    };
  }
}

/**
 * Check if a color is considered dark
 */
function isDarkColor(color: string): boolean {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Dynamic Text Scaling Support
 */

/**
 * Check if dynamic text scaling is enabled
 */
export async function isDynamicTextEnabled(): Promise<boolean> {
  try {
    return typeof (AccessibilityInfo as any).isBoldTextEnabled === 'function'
      ? await (AccessibilityInfo as any).isBoldTextEnabled()
      : false;
  } catch {
    return false;
  }
}

/**
 * Calculate dynamic text scale factor
 */
export function getDynamicTextScale(
  baseSize: number,
  userScale: number = 1.0,
  maxScale: number = 2.0
): number {
  const scale = Math.min(Math.max(userScale, 0.8), maxScale);
  return Math.round(baseSize * scale);
}

/**
 * Voice Control Compatibility
 */

/**
 * Generate voice control accessibility hints
 */
export function generateVoiceControlHint(
  action: string,
  element: string,
  state?: string
): string {
  const parts = [`${action} ${element}`];
  if (state) parts.push(state);
  return parts.join(', ');
}

/**
 * Check if voice control is enabled
 */
export async function isVoiceControlEnabled(): Promise<boolean> {
  try {
    // This is a simplified check - in a real app you'd want more sophisticated detection
    const screenReader = await AccessibilityInfo.isScreenReaderEnabled();
    const boldText =
      typeof (AccessibilityInfo as any).isBoldTextEnabled === 'function'
        ? await (AccessibilityInfo as any).isBoldTextEnabled()
        : false;
    return screenReader || boldText;
  } catch {
    return false;
  }
}

/**
 * Focus Management System
 */

class FocusManager {
  private focusableElements: Map<string, FocusableElement> = new Map();
  private currentFocus: string | null = null;
  private focusOrder: string[] = [];

  /**
   * Register a focusable element
   */
  register(element: FocusableElement): void {
    this.focusableElements.set(element.id, element);
    this.focusOrder.push(element.id);
  }

  /**
   * Unregister a focusable element
   */
  unregister(id: string): void {
    this.focusableElements.delete(id);
    this.focusOrder = this.focusOrder.filter(elementId => elementId !== id);
  }

  /**
   * Focus an element by ID
   */
  focus(id: string): boolean {
    const element = this.focusableElements.get(id);
    if (element && element.ref.current) {
      element.ref.current.focus?.();
      this.currentFocus = id;
      element.onFocus?.();
      return true;
    }
    return false;
  }

  /**
   * Focus the next element in order
   */
  focusNext(): boolean {
    if (!this.currentFocus) {
      return this.focusFirst();
    }

    const currentIndex = this.focusOrder.indexOf(this.currentFocus);
    const nextIndex = (currentIndex + 1) % this.focusOrder.length;
    return this.focus(this.focusOrder[nextIndex]);
  }

  /**
   * Focus the previous element in order
   */
  focusPrevious(): boolean {
    if (!this.currentFocus) {
      return this.focusLast();
    }

    const currentIndex = this.focusOrder.indexOf(this.currentFocus);
    const previousIndex =
      currentIndex === 0 ? this.focusOrder.length - 1 : currentIndex - 1;
    return this.focus(this.focusOrder[previousIndex]);
  }

  /**
   * Focus the first element
   */
  focusFirst(): boolean {
    if (this.focusOrder.length === 0) return false;
    return this.focus(this.focusOrder[0]);
  }

  /**
   * Focus the last element
   */
  focusLast(): boolean {
    if (this.focusOrder.length === 0) return false;
    return this.focus(this.focusOrder[this.focusOrder.length - 1]);
  }

  /**
   * Get current focus
   */
  getCurrentFocus(): string | null {
    return this.currentFocus;
  }

  /**
   * Clear current focus
   */
  clearFocus(): void {
    this.currentFocus = null;
  }

  /**
   * Get all focusable elements
   */
  getFocusableElements(): FocusableElement[] {
    return Array.from(this.focusableElements.values());
  }
}

// Global focus manager instance
export const focusManager = new FocusManager();

/**
 * Accessibility Validation Utilities
 */

/**
 * Validate touch target size meets accessibility standards
 */
export function validateTouchTarget(
  width: number,
  height: number,
  minSize: number = 44
): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];

  if (width < minSize) {
    issues.push(
      `Touch target width (${width}px) is below minimum (${minSize}px)`
    );
  }

  if (height < minSize) {
    issues.push(
      `Touch target height (${height}px) is below minimum (${minSize}px)`
    );
  }

  return {
    isValid: issues.length === 0,
    issues,
  };
}

/**
 * Validate color contrast ratio
 */
export function validateColorContrast(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA'
): { isValid: boolean; ratio: number; level: 'AA' | 'AAA' | 'fail' } {
  const ratio = calculateContrastRatio(foreground, background);

  let isValid = false;
  let requiredLevel: 'AA' | 'AAA' | 'fail' = 'fail';

  if (level === 'AA') {
    isValid = ratio >= 4.5;
    requiredLevel = ratio >= 4.5 ? 'AA' : 'fail';
  } else if (level === 'AAA') {
    isValid = ratio >= 7.0;
    requiredLevel = ratio >= 7.0 ? 'AAA' : 'fail';
  }

  return { isValid, ratio, level: requiredLevel };
}

/**
 * Calculate contrast ratio between two colors
 */
function calculateContrastRatio(
  foreground: string,
  background: string
): number {
  const getLuminance = (color: string): number => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const sRGB = [r, g, b].map(c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
  };

  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Accessibility State Management
 */

/**
 * Create accessibility state object
 */
export function createAccessibilityState(
  state: Partial<AccessibilityState> = {}
): AccessibilityState {
  return {
    disabled: false,
    selected: false,
    checked: false,
    busy: false,
    expanded: false,
    pressed: false,
    ...state,
  };
}

/**
 * Accessibility Event Handlers
 */

/**
 * Create accessibility event handlers
 */
export function createAccessibilityHandlers(
  onAccessibilityActivate?: () => void,
  onAccessibilityEscape?: () => void,
  onAccessibilityIncrement?: () => void,
  onAccessibilityDecrement?: () => void
) {
  return {
    onAccessibilityActivate,
    onAccessibilityEscape,
    onAccessibilityIncrement,
    onAccessibilityDecrement,
  };
}

/**
 * Accessibility Announcement Utilities
 */

/**
 * Announce loading state
 */
export function announceLoading(message: string = 'Loading'): void {
  announceToScreenReader(`${message}, please wait`);
}

/**
 * Announce completion
 */
export function announceCompletion(message: string): void {
  announceToScreenReader(message);
}

/**
 * Announce error
 */
export function announceError(message: string): void {
  announceToScreenReader(`Error: ${message}`);
}

/**
 * Announce success
 */
export function announceSuccess(message: string): void {
  announceToScreenReader(`Success: ${message}`);
}

/**
 * Accessibility Testing Utilities
 */

/**
 * Mock accessibility info for testing
 */
export function mockAccessibilityInfo() {
  return {
    isScreenReaderEnabled: jest.fn().mockResolvedValue(false),
    isBoldTextEnabled: jest.fn().mockResolvedValue(false),
    isHighContrastEnabled: jest.fn().mockResolvedValue(false),
    isReduceMotionEnabled: jest.fn().mockResolvedValue(false),
    announceForAccessibility: jest.fn(),
  };
}

/**
 * Accessibility Constants
 */
export const ACCESSIBILITY_CONSTANTS = {
  MIN_TOUCH_TARGET_SIZE: 44,
  MIN_TOUCH_TARGET_SIZE_LARGE: 48,
  MIN_CONTRAST_RATIO_AA: 4.5,
  MIN_CONTRAST_RATIO_AAA: 7.0,
  FOCUS_VISIBLE_DURATION: 200,
  ANNOUNCEMENT_DELAY: 100,
} as const;
